import type { VizDataFormat } from './DataTable';

/** Default body rows visible before vertical scroll (Ask Asper agentic right panel). */
export const DATA_TABLE_MAX_PREVIEW_BODY_ROWS = 10;

export interface TablePreviewStats {
    shownRows: number;
    totalRows: number;
    shownColumns: number;
    totalColumns: number;
}

function readOptionalTotal(vizData: Record<string, unknown>, ...keys: string[]): number | undefined {
    for (const key of keys) {
        const raw = vizData[key];
        if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
            return Math.floor(raw);
        }
    }
    return undefined;
}

/** Row 0 in `data` is always the column header row; body-only counts exclude it. */
function bodyRowCountFromGrid(grid: unknown[][]): number {
    return Math.max(0, grid.length - 1);
}

/**
 * Some backends send totals that include the header row (same as `data.length`).
 * Normalize to body-only row counts for preview footer text.
 */
function normalizeBackendRowTotal(rawTotal: number | undefined, grid: unknown[][]): number {
    const dataRowCount = bodyRowCountFromGrid(grid);
    if (rawTotal == null) {
        return dataRowCount;
    }
    if (rawTotal === grid.length) {
        return Math.max(0, rawTotal - 1);
    }
    return rawTotal;
}

function isAntdTableDataRow(row: HTMLElement): boolean {
    if (row.classList.contains('ant-table-measure-row')) return false;
    if (row.classList.contains('ant-table-placeholder')) return false;
    if (row.getAttribute('aria-hidden') === 'true') return false;
    return true;
}

function getAntdTableBodyRows(tableRoot: HTMLElement): NodeListOf<HTMLElement> | undefined {
    const bodyTable =
        tableRoot.querySelector<HTMLElement>('.ant-table-body table') ??
        tableRoot.querySelector<HTMLTableElement>('table');
    const rows = bodyTable?.querySelectorAll<HTMLElement>('tbody tr');
    return rows && rows.length > 0 ? rows : undefined;
}

/**
 * Row/column counts for the table preview line in PDF export.
 * Supports optional backend totals on viz payload (`total_row_count`, `total_column_count`).
 */
export function getTablePreviewStats(
    vizData: VizDataFormat | undefined,
    maxScrollBodyRows: number = DATA_TABLE_MAX_PREVIEW_BODY_ROWS,
): TablePreviewStats | null {
    if (!vizData?.data || !Array.isArray(vizData.data) || vizData.data.length === 0) {
        return null;
    }

    const grid = vizData.data as unknown[][];
    const headers = grid[0];
    if (!Array.isArray(headers) || headers.length === 0) {
        return null;
    }

    const payload = vizData as VizDataFormat & Record<string, unknown>;
    const dataRowCount = bodyRowCountFromGrid(grid);
    const columnsInPayload = headers.length;
    const totalColumns = readOptionalTotal(payload, 'total_column_count', 'totalColumnCount') ?? columnsInPayload;
    /** Refined at PDF export via {@link countVisibleAntdTableColumns} to match the captured viewport. */
    const shownColumns = Math.min(columnsInPayload, totalColumns);
    const totalRows = normalizeBackendRowTotal(
        readOptionalTotal(payload, 'total_row_count', 'totalRowCount'),
        grid,
    );

    const cappedRows =
        maxScrollBodyRows > 0 ? Math.min(dataRowCount, maxScrollBodyRows) : dataRowCount;
    const shownRows = Math.min(cappedRows, totalRows);

    return {
        shownRows,
        totalRows,
        shownColumns,
        totalColumns,
    };
}

export function formatTablePreviewSummary(stats: TablePreviewStats): string {
    return `Table preview: ${stats.shownColumns} of ${stats.totalColumns} columns, ${stats.shownRows} of ${stats.totalRows} rows`;
}

interface ViewportBounds {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

/**
 * html2canvas rasterizes a scroll viewport's *content box* (`clientWidth`/`clientHeight`),
 * which excludes borders and the strip occupied by scrollbars. `getBoundingClientRect()`
 * returns the *border box*, so a row/column clipped only by a border or scrollbar is
 * reported as visible by rect math yet is cut off in the captured image. Measure against
 * the content box so the preview count matches what the export actually paints.
 *
 * In jsdom (unit tests) `clientWidth`/`clientHeight` are 0, so we fall back to the
 * (mocked) border-box rect and behavior is unchanged.
 */
function getViewportContentBounds(viewport: HTMLElement): ViewportBounds {
    const rect = viewport.getBoundingClientRect();
    const styles = typeof getComputedStyle === 'function' ? getComputedStyle(viewport) : null;
    const borderTop = styles ? parseFloat(styles.borderTopWidth) || 0 : 0;
    const borderLeft = styles ? parseFloat(styles.borderLeftWidth) || 0 : 0;
    const top = rect.top + borderTop;
    const left = rect.left + borderLeft;
    return {
        top,
        left,
        bottom: viewport.clientHeight > 0 ? top + viewport.clientHeight : rect.bottom,
        right: viewport.clientWidth > 0 ? left + viewport.clientWidth : rect.right,
    };
}

/**
 * Sub-pixel slack only. A larger tolerance lets a row/column clipped by a full border
 * still count, which is exactly what made the preview over-report (e.g. "9" when 8 paint).
 */
const FULL_COLUMN_VISIBLE_TOLERANCE_PX = 0.5;

function isColumnFullyVisibleInViewport(cellRect: DOMRect, viewportRect: ViewportBounds): boolean {
    if (cellRect.width <= 0) return false;
    const visibleLeft = Math.max(cellRect.left, viewportRect.left);
    const visibleRight = Math.min(cellRect.right, viewportRect.right);
    const visibleWidth = visibleRight - visibleLeft;
    return visibleWidth >= cellRect.width - FULL_COLUMN_VISIBLE_TOLERANCE_PX;
}

/**
 * Count ant Design table columns fully visible in the horizontal scroll viewport
 * (matches what html2canvas captures; clipped edge columns are not counted).
 */
export function countVisibleAntdTableColumns(root: HTMLElement): number | undefined {
    const tableRoot =
        root.querySelector<HTMLElement>('.data-table-container .ant-table-container') ??
        root.querySelector<HTMLElement>('.ant-table-container');
    if (!tableRoot) return undefined;

    const viewport =
        tableRoot.querySelector<HTMLElement>('.ant-table-body') ??
        tableRoot.querySelector<HTMLElement>('.ant-table-header') ??
        tableRoot.querySelector<HTMLElement>('.ant-table-content') ??
        tableRoot;
    const viewportRect = getViewportContentBounds(viewport);

    const bodyRow = tableRoot.querySelector('tbody tr');
    const bodyCells = bodyRow?.querySelectorAll<HTMLElement>('td');
    const cells =
        bodyCells && bodyCells.length > 0
            ? bodyCells
            : tableRoot.querySelectorAll<HTMLElement>('.ant-table-thead th, thead th');
    if (!cells.length) return undefined;

    let visible = 0;
    cells.forEach((cell) => {
        if (isColumnFullyVisibleInViewport(cell.getBoundingClientRect(), viewportRect)) {
            visible += 1;
        }
    });

    return visible > 0 ? visible : undefined;
}

/** Sub-pixel slack only (see {@link FULL_COLUMN_VISIBLE_TOLERANCE_PX}). */
const FULL_ROW_VISIBLE_TOLERANCE_PX = 0.5;

function isRowFullyVisibleInViewport(rowRect: DOMRect, viewportRect: ViewportBounds): boolean {
    if (rowRect.height <= 0) return false;
    const visibleTop = Math.max(rowRect.top, viewportRect.top);
    const visibleBottom = Math.min(rowRect.bottom, viewportRect.bottom);
    const visibleHeight = visibleBottom - visibleTop;
    return visibleHeight >= rowRect.height - FULL_ROW_VISIBLE_TOLERANCE_PX;
}

/**
 * On screen the sticky column header sits in its own `.ant-table-header` element,
 * so the whole `.ant-table-body` scroll viewport is free for data rows. On export
 * {@link flattenAntdScrollTableForExport} merges that header `<thead>` into the body
 * table without growing the viewport, so the merged header consumes the top slice and
 * one fewer data row is painted. Subtract the header height so the counted rows match
 * what the export actually renders (and never count the header itself as a row).
 *
 * Only the scroll layout (separate `.ant-table-header`) is affected; single-table
 * layouts keep the header inline above the body rows, so their row rects already
 * sit below it and need no adjustment.
 */
function getMergedHeaderHeight(tableRoot: HTMLElement, viewport: HTMLElement): number {
    const header = tableRoot.querySelector<HTMLElement>('.ant-table-header');
    if (!header || viewport !== tableRoot.querySelector('.ant-table-body')) return 0;
    const height = header.getBoundingClientRect().height;
    return Number.isFinite(height) && height > 0 ? height : 0;
}

/**
 * Count ant Design table body rows fully visible in the vertical scroll viewport
 * (matches what html2canvas captures; clipped edge rows are not counted).
 */
export function countVisibleAntdTableBodyRows(root: HTMLElement): number | undefined {
    const tableRoot =
        root.querySelector<HTMLElement>('.data-table-container .ant-table-container') ??
        root.querySelector<HTMLElement>('.ant-table-container');
    if (!tableRoot) return undefined;

    const viewport =
        tableRoot.querySelector<HTMLElement>('.ant-table-body') ??
        tableRoot.querySelector<HTMLElement>('.ant-table-content') ??
        tableRoot;
    const viewportRect = getViewportContentBounds(viewport);
    viewportRect.bottom -= getMergedHeaderHeight(tableRoot, viewport);

    const rows = getAntdTableBodyRows(tableRoot);
    if (!rows?.length) return undefined;

    let visible = 0;
    rows.forEach((row) => {
        if (!isAntdTableDataRow(row)) return;
        if (isRowFullyVisibleInViewport(row.getBoundingClientRect(), viewportRect)) {
            visible += 1;
        }
    });

    return visible > 0 ? visible : undefined;
}

/** Apply live DOM row/column counts before writing export preview lines. */
export function resolveTablePreviewStatsForExport(
    element: HTMLElement,
    stored: TablePreviewStats,
): TablePreviewStats {
    const visibleColumns = countVisibleAntdTableColumns(element);
    const visibleRows = countVisibleAntdTableBodyRows(element);
    return {
        ...stored,
        shownColumns:
            visibleColumns != null
                ? Math.min(visibleColumns, stored.totalColumns)
                : stored.shownColumns,
        shownRows:
            visibleRows != null ? Math.min(visibleRows, stored.totalRows) : stored.shownRows,
    };
}
